import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Drawer, Progress, Space, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { downloadStore, DownloadItem } from 'store/downloadStore';

const { Text } = Typography;

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const computePercent = (item: DownloadItem): number => {
  if (item.status === 'completed') return 100;
  if (!item.totalBytes || item.totalBytes <= 0) return 0;
  return Math.min(100, Math.round((item.receivedBytes / item.totalBytes) * 100));
};

const statusTag = (status: DownloadItem['status']): React.ReactNode => {
  switch (status) {
    case 'pending':
      return <Tag>Waiting</Tag>;
    case 'downloading':
      return <Tag color="processing">Downloading</Tag>;
    case 'completed':
      return <Tag color="success">Done</Tag>;
    case 'failed':
      return <Tag color="error">Failed</Tag>;
    case 'cancelled':
      return <Tag color="warning">Cancelled</Tag>;
    default:
      return null;
  }
};

const DownloadItemCard: React.FC<{ item: DownloadItem }> = observer(({ item }) => {
  const isRunning = item.status === 'pending' || item.status === 'downloading';
  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong style={{ fontSize: 13 }}>
            {item.label}
          </Text>
          {statusTag(item.status)}
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
          {item.filename}
        </Text>
        <Progress
          percent={computePercent(item)}
          size="small"
          status={
            item.status === 'failed' || item.status === 'cancelled'
              ? 'exception'
              : item.status === 'completed'
              ? 'success'
              : 'active'
          }
        />
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {formatBytes(item.receivedBytes)}
            {item.totalBytes ? ` / ${formatBytes(item.totalBytes)}` : ''}
          </Text>
          {isRunning ? (
            <Button
              size="small"
              type="text"
              danger
              onClick={() => downloadStore.cancel(item.id)}
            >
              Cancel
            </Button>
          ) : (
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => downloadStore.removeItem(item.id)}
            />
          )}
        </Space>
        {item.error && (
          <Text type="danger" style={{ fontSize: 11 }}>
            {item.error}
          </Text>
        )}
      </Space>
    </Card>
  );
});

/**
 * 라우팅과 무관하게 살아있는 최상위 다운로드 매니저.
 * StreamSaver + SW를 통해 페이지 이동해도 다운로드 계속됨.
 */
export const DownloadDock: React.FC = observer(() => {
  const { items, activeCount, panelOpen } = downloadStore;

  if (items.length === 0) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 1100,
        }}
      >
        <Badge count={activeCount} offset={[-6, 6]}>
          <Button
            type="primary"
            shape="round"
            icon={<DownloadOutlined />}
            onClick={() => downloadStore.setPanelOpen(true)}
          >
            Downloads ({items.length})
          </Button>
        </Badge>
      </div>

      <Drawer
        title="Downloads"
        placement="right"
        width={420}
        open={panelOpen}
        onClose={() => downloadStore.setPanelOpen(false)}
        extra={
          <Button size="small" onClick={() => downloadStore.clearFinished()}>
            Clear finished
          </Button>
        }
      >
        {items.map(item => (
          <DownloadItemCard key={item.id} item={item} />
        ))}
      </Drawer>
    </>
  );
});
